import { Voice } from '@/types/voice/voice';
import { Button } from "@/components/ui/button";
import { ChevronDown, Loader2, Play, Search, Heart, Sparkles, Check, Mic } from "lucide-react";
import { cn } from "@/lib/utils";
import { getGenieImage } from '@/utils/image/genieAvatars';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

interface VoiceDropdownProps {
  value: string;
  currentVoice?: Voice;
  voices?: Voice[];
  voicesLoading: boolean;
  favoriteVoices: Voice[];
  recommendedVoices: Voice[];
  favoriteVoiceIds: Set<string>;
  playingVoiceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVoiceSelect: (voiceId: string) => void;
  onPlayPreview: (voice: Voice, e?: React.MouseEvent) => void;
  onToggleFavorite: (voice: Voice, e?: React.MouseEvent) => void;
  onTestVoice: (voice: Voice, e?: React.MouseEvent) => void;
  onOpenModal: () => void;
  className?: string;
  restrictToVoices?: boolean;
  hidePlayButton?: boolean;
}

interface VoiceDropdownItemProps {
  voice: Voice;
  isSelected: boolean;
  isFavorited: boolean;
  playingVoiceId: string | null;
  onSelect: (voiceId: string) => void;
  onPlay: (voice: Voice, e?: React.MouseEvent) => void;
  onToggleFavorite: (voice: Voice, e?: React.MouseEvent) => void;
  onTest: (voice: Voice, e?: React.MouseEvent) => void;
}

const VoiceDropdownItem = ({
  voice,
  isSelected,
  isFavorited,
  playingVoiceId,
  onSelect,
  onPlay,
  onToggleFavorite,
  onTest
}: VoiceDropdownItemProps) => {
  return (
    <div
      onClick={() => onSelect(voice.id)}
      className={cn(
        "px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors border-l-4",
        isSelected ? "bg-blue-50 border-blue-500" : "border-transparent"
      )}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10">
          <img
            src={getGenieImage(voice.id, voice.labels?.gender as 'male' | 'female' | 'neutral' || 'neutral')}
            alt={voice.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = '/helpgenie_logo.png';
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn("font-medium text-sm truncate", isSelected && "text-blue-600")}>
              {voice.name}
            </span>
            {isSelected && <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />}
          </div>
          {voice.labels?.accent && (
            <p className="text-xs text-gray-500 truncate">{voice.labels.accent}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => onPlay(voice, e)}
            disabled={playingVoiceId === voice.id}
            className="h-7 w-7 p-0"
            title="Preview voice"
          >
            {playingVoiceId === voice.id ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Play className="h-3 w-3" />
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => onTest(voice, e)}
            className="h-7 w-7 p-0"
            title="Test with custom text"
          >
            <Mic className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => onToggleFavorite(voice, e)}
            className="h-7 w-7 p-0"
            title={isFavorited ? "Remove from favorites" : "Add to favorites"}
          >
            <Heart className={cn("h-3 w-3", isFavorited && "fill-pink-500 text-pink-500")} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const VoiceDropdown = ({
  value,
  currentVoice,
  voices,
  voicesLoading,
  favoriteVoices,
  recommendedVoices,
  favoriteVoiceIds,
  playingVoiceId,
  open,
  onOpenChange,
  onVoiceSelect,
  onPlayPreview,
  onToggleFavorite,
  onTestVoice,
  onOpenModal,
  className,
  restrictToVoices = false,
  hidePlayButton = false,
}: VoiceDropdownProps) => {
  const displayVoice = currentVoice || voices?.find(v => v.id === value);

  return (
    <div className={cn("w-full flex gap-2", className)}>
      <DropdownMenu open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "flex-1 justify-between h-12 px-4",
              value && "border-blue-500 bg-blue-50/50"
            )}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {displayVoice ? (
                <>
                  <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={getGenieImage(displayVoice.id, displayVoice.labels?.gender as 'male' | 'female' | 'neutral' || 'neutral')}
                      alt={displayVoice.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = '/helpgenie_logo.png';
                      }}
                    />
                  </div>
                  <span className="truncate font-medium">{displayVoice.name}</span>
                  {displayVoice.labels?.accent && (
                    <span className="text-xs text-gray-500 truncate hidden sm:inline">({displayVoice.labels.accent})</span>
                  )}
                </>
              ) : (
                <span className="text-gray-500">Select a voice...</span>
              )}
            </div>
            <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[var(--radix-dropdown-menu-trigger-width)] max-w-[95vw] max-h-[70vh] sm:max-h-[500px] overflow-y-auto p-0"
          align="start"
          sideOffset={5}
        >
          {voicesLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-blue-500" />
              <p className="text-sm text-gray-500">Loading voices...</p>
            </div>
          ) : (
            <>
              {displayVoice && (
                <>
                  <DropdownMenuLabel className="px-3 py-2 flex items-center gap-1 text-blue-600">
                    <Check className="h-3 w-3" />
                    Currently Selected
                  </DropdownMenuLabel>
                  <VoiceDropdownItem
                    voice={displayVoice}
                    isSelected={true}
                    isFavorited={favoriteVoiceIds.has(displayVoice.id)}
                    playingVoiceId={playingVoiceId}
                    onSelect={onVoiceSelect}
                    onPlay={onPlayPreview}
                    onToggleFavorite={onToggleFavorite}
                    onTest={onTestVoice}
                  />
                  <DropdownMenuSeparator />
                </>
              )}

              {favoriteVoices.length > 0 && (
                <>
                  <DropdownMenuLabel className="px-3 py-1.5 flex items-center gap-1">
                    <Heart className="h-3 w-3" />
                    Favorites
                  </DropdownMenuLabel>
                  {favoriteVoices.slice(0, 5).map((voice) => (
                    <VoiceDropdownItem
                      key={`fav-${voice.id}`}
                      voice={voice}
                      isSelected={value === voice.id}
                      isFavorited={true}
                      playingVoiceId={playingVoiceId}
                      onSelect={onVoiceSelect}
                      onPlay={onPlayPreview}
                      onToggleFavorite={onToggleFavorite}
                      onTest={onTestVoice}
                    />
                  ))}
                  <DropdownMenuSeparator />
                </>
              )}

              {recommendedVoices.length > 0 && (
                <>
                  <DropdownMenuLabel className="px-3 py-1.5 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    {restrictToVoices ? "Preselected" : "Recommended"}
                  </DropdownMenuLabel>
                  {(restrictToVoices ? recommendedVoices : recommendedVoices.slice(0, 10)).map((voice) => (
                    <VoiceDropdownItem
                      key={`rec-${voice.id}`}
                      voice={voice}
                      isSelected={value === voice.id}
                      isFavorited={favoriteVoiceIds.has(voice.id)}
                      playingVoiceId={playingVoiceId}
                      onSelect={onVoiceSelect}
                      onPlay={onPlayPreview}
                      onToggleFavorite={onToggleFavorite}
                      onTest={onTestVoice}
                    />
                  ))}
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  onOpenChange(false);
                  onOpenModal();
                }}
                className="px-3 py-2 cursor-pointer"
              >
                <Search className="h-4 w-4 mr-2" />
                Browse All Voices
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {displayVoice?.preview_url && !hidePlayButton && (
        <Button
          type="button"
          variant="outline"
          onClick={() => onPlayPreview(displayVoice)}
          disabled={playingVoiceId === displayVoice.id}
          className="px-3 h-12 flex-shrink-0"
          title="Play voice preview"
        >
          {playingVoiceId === displayVoice.id ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </Button>
      )}
    </div>
  );
};
